import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { SidebarLayout, PageHeader, Card } from '../components/layout';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../hooks/use-auth';
import { useCancelledOrders } from '../hooks/use-orders';
import { useUserApprovals } from '../hooks/use-user-approvals';
import { OrderSummaryDto } from '../types';
import { formatCurrency, formatDate, sortableHeaderProps } from '../utils';
import { useDebounce } from '../hooks/use-debounce';
import { useMinDurationFlag } from '../hooks/use-min-duration-flag';

export function RejectedOrdersPage() {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  // Get division ID from user approvals hook for consistency
  const { divisionId } = useUserApprovals();

  // Filter state
  const [searchInput, setSearchInput] = useState('');
  const [submitterInput, setSubmitterInput] = useState('');
  const [pageSize, setPageSize] = useState(50);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({ sortBy: 'updatedAt', sortOrder: 'desc' as 'asc' | 'desc' });

  // Debounced values
  const debouncedSearch = useDebounce(searchInput, 300);
  const debouncedSubmitter = useDebounce(submitterInput, 300);

  const filters = useMemo(() => ({
    page: currentPage,
    limit: pageSize,
    search: debouncedSearch || undefined,
    submitterId: debouncedSubmitter || undefined,
    sortBy: sortConfig.sortBy,
    sortOrder: sortConfig.sortOrder
  }), [currentPage, pageSize, debouncedSearch, debouncedSubmitter, sortConfig]);

  // Fetch cancelled orders
  const { 
    data: cancelledOrdersData, 
    isLoading,
    isFetching, 
    error, 
    refetch 
  } = useCancelledOrders(
    divisionId || '', 
    filters,
    !!divisionId && isAuthenticated
  );
  const showFetchingOverlay = useMinDurationFlag(isFetching && !isLoading);

  const cancelledOrders = cancelledOrdersData?.items || [];
  const totalCount = cancelledOrdersData?.totalCount || 0;
  const totalPages = cancelledOrdersData?.totalPages || 1;

  const handleSort = (field: string) => {
    setSortConfig(prev => ({
      sortBy: field,
      sortOrder: prev.sortBy === field && prev.sortOrder === 'asc' ? 'desc' : 'asc'
    }));
    setCurrentPage(1);
  };

  const getSortIcon = (field: string) => {
    if (sortConfig.sortBy !== field) return <i className="fa-solid fa-sort text-gray-500 dark:text-secondary-400 ml-2"></i>;
    return sortConfig.sortOrder === 'asc' 
      ? <i className="fa-solid fa-sort-up text-blue-600 dark:text-blue-400 ml-2"></i>
      : <i className="fa-solid fa-sort-down text-blue-600 dark:text-blue-400 ml-2"></i>;
  };

  if (!isAuthenticated) {
    return (
      <SidebarLayout>
        <div className="py-4">
          <Card>
            <div className="p-6 text-center">
              <p className="text-gray-600 dark:text-secondary-400">Please sign in to view rejected orders.</p>
            </div>
          </Card>
        </div>
      </SidebarLayout>
    );
  }

  if (error) {
    return (
      <SidebarLayout>
        <div className="py-4">
          <PageHeader
            title="Rejected Orders"
            subtitle="Orders that have been rejected or declined"
          />
          <Card>
            <div className="p-6 text-center">
              <p className="text-red-600 dark:text-red-400 mb-4">Error loading rejected orders</p>
              <Button onClick={() => refetch()}>Try Again</Button>
            </div>
          </Card>
        </div>
      </SidebarLayout>
    );
  }

  const getStatusBadgeVariant = (status: string) => {
    switch (status.toLowerCase()) {
      case 'cancelled': return 'error';
      case 'declined': return 'error';
      case 'timeout': return 'warning';
      default: return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status.toLowerCase()) {
      case 'cancelled': return 'Cancelled';
      case 'declined': return 'Declined';
      case 'timeout': return 'Timed Out';
      default: return status;
    }
  };

  const handleViewOrder = (orderId: string) => {
    navigate(`/order-detail/${orderId}`);
  };

  return (
    <SidebarLayout>
      <div className="py-6">
        <PageHeader
          title="Cancelled Orders"
          subtitle="Orders that were declined, cancelled, or timed out during the workflow"
        />

        {/* Stats Cards */}
        <div className="grid grid-cols-3 gap-6 mb-6">
          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-700 dark:text-secondary-200">Total Rejected</h3>
                <p className="text-3xl font-bold text-red-600 dark:text-red-400 mt-2">{totalCount}</p>
              </div>
              <div className="bg-red-100 dark:bg-red-900 p-3 rounded-full">
                <i className="fa-solid fa-ban text-red-600 dark:text-red-400 text-xl"></i>
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-700 dark:text-secondary-200">This Month</h3>
                <p className="text-3xl font-bold text-orange-600 dark:text-orange-400 mt-2">
                  {cancelledOrders.filter(order => {
                    const orderDate = new Date(order.updatedAt);
                    const now = new Date();
                    return orderDate.getMonth() === now.getMonth() && 
                           orderDate.getFullYear() === now.getFullYear();
                  }).length}
                </p>
              </div>
              <div className="bg-orange-100 dark:bg-orange-900 p-3 rounded-full">
                <i className="fa-solid fa-calendar text-orange-600 dark:text-orange-400 text-xl"></i>
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-700 dark:text-secondary-200">Declined</h3>
                <p className="text-3xl font-bold text-purple-600 dark:text-purple-400 mt-2">
                  {cancelledOrders.filter(order => 
                    order.status.toLowerCase() === 'declined'
                  ).length}
                </p>
              </div>
              <div className="bg-purple-100 dark:bg-purple-900 p-3 rounded-full">
                <i className="fa-solid fa-circle-xmark text-purple-600 dark:text-purple-400 text-xl"></i>
              </div>
            </div>
          </Card>
        </div>

        {/* Rejected Orders Table */}
        <Card>
          {/* Toolbar */}
          <div className="px-6 pt-4 pb-3 flex items-center justify-between border-b border-gray-100 dark:border-secondary-700">
            <div className="flex items-center gap-4">
              <Button onClick={() => refetch()} variant="outline" size="sm">
                <i className="fa-solid fa-refresh mr-2"></i>
                Refresh
              </Button>
              <div className="flex items-center gap-2">
                <label className="text-sm text-gray-600 dark:text-secondary-400">Page Size:</label>
                <select
                  value={pageSize}
                  onChange={(e) => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}
                  className="px-2 py-1 border border-gray-300 dark:border-secondary-600 rounded text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-secondary-900 text-secondary-900 dark:text-secondary-100"
                >
                  <option value="25">25</option>
                  <option value="50">50</option>
                  <option value="100">100</option>
                </select>
              </div>
            </div>
            {(searchInput || submitterInput) && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => { setSearchInput(''); setSubmitterInput(''); setCurrentPage(1); }}
              >
                <i className="fa-solid fa-times mr-2"></i>
                Clear Filters
              </Button>
            )}
          </div>

          <div className="p-6 relative">
            {/* Loading overlay during filter changes */}
            {showFetchingOverlay && (
              <div className="absolute inset-0 bg-white/60 dark:bg-secondary-800/60 flex items-start justify-center pt-16 z-10 rounded-b-lg">
                <div className="bg-white dark:bg-secondary-800 shadow-lg rounded-lg px-4 py-3 flex items-center gap-3">
                  <i className="fa-solid fa-spinner fa-spin text-blue-600 dark:text-blue-400"></i>
                  <span className="text-sm font-medium text-gray-700 dark:text-secondary-200">Updating results...</span>
                </div>
              </div>
            )}

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-secondary-700">
                <thead className="bg-gray-50 dark:bg-secondary-900">
                  <tr>
                    <th
                      className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-secondary-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-secondary-600"
                      {...sortableHeaderProps(() => handleSort('orderNumber'), sortConfig.sortBy === 'orderNumber', sortConfig.sortOrder)}
                    >
                      <div className="flex items-center">Order {getSortIcon('orderNumber')}</div>
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-secondary-400 uppercase tracking-wider">
                      Submitter
                    </th>
                    <th
                      className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-secondary-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-secondary-600"
                      {...sortableHeaderProps(() => handleSort('totalAmount'), sortConfig.sortBy === 'totalAmount', sortConfig.sortOrder)}
                    >
                      <div className="flex items-center">Amount {getSortIcon('totalAmount')}</div>
                    </th>
                    <th
                      className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-secondary-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-secondary-600"
                      {...sortableHeaderProps(() => handleSort('updatedAt'), sortConfig.sortBy === 'updatedAt', sortConfig.sortOrder)}
                    >
                      <div className="flex items-center">Rejected {getSortIcon('updatedAt')}</div>
                    </th>
                    <th
                      className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-secondary-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-secondary-600"
                      {...sortableHeaderProps(() => handleSort('status'), sortConfig.sortBy === 'status', sortConfig.sortOrder)}
                    >
                      <div className="flex items-center">Status {getSortIcon('status')}</div>
                    </th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 dark:text-secondary-400 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                  {/* Filter Row */}
                  <tr className="bg-white dark:bg-secondary-800 border-t border-gray-200 dark:border-secondary-700">
                    <th className="px-4 py-2">
                      <input
                        type="text"
                        placeholder="Filter..."
                        value={searchInput}
                        onChange={(e) => { setSearchInput(e.target.value); setCurrentPage(1); }}
                        onClick={(e) => e.stopPropagation()}
                        className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-secondary-600 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-secondary-900 text-secondary-900 dark:text-secondary-100"
                      />
                    </th>
                    <th className="px-4 py-2">
                      <input
                        type="text"
                        placeholder="Filter..."
                        value={submitterInput}
                        onChange={(e) => { setSubmitterInput(e.target.value); setCurrentPage(1); }}
                        onClick={(e) => e.stopPropagation()}
                        className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-secondary-600 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-secondary-900 text-secondary-900 dark:text-secondary-100"
                      />
                    </th>
                    <th className="px-4 py-2"></th>
                    <th className="px-4 py-2"></th>
                    <th className="px-4 py-2"></th>
                    <th className="px-4 py-2"></th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-secondary-800 divide-y divide-gray-200 dark:divide-secondary-700">
                  {isLoading ? (
                    <tr>
                      <td colSpan={6} className="text-center py-12">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                        <p className="text-gray-600 dark:text-secondary-400">Loading rejected orders...</p>
                      </td>
                    </tr>
                  ) : cancelledOrders.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="text-center py-12">
                        <i className="fa-solid fa-check-circle text-4xl text-green-300 mb-4"></i>
                        <p className="text-gray-600 dark:text-secondary-400 mb-2">No rejected orders found</p>
                        <p className="text-sm text-gray-500 dark:text-secondary-400">
                          {searchInput || submitterInput
                            ? 'Try adjusting your filter terms'
                            : 'Great! There are no rejected orders at this time'}
                        </p>
                      </td>
                    </tr>
                  ) : (
                    cancelledOrders.map((order) => (
                      <tr key={order.id} data-testid={`order-row-${order.id}`} className="hover:bg-gray-50 dark:hover:bg-secondary-700">
                        <td className="px-4 py-3">
                          <div>
                            <a
                              data-testid={`order-number-${order.id}`}
                              className="font-mono text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 hover:underline cursor-pointer"
                              href={`/order-detail/${order.id}`}
                              onClick={(e) => { e.preventDefault(); handleViewOrder(order.id); }}
                              title="Click to view order details"
                            >
                              {order.orderNumber}
                            </a>
                            <div className="text-sm text-gray-500 dark:text-secondary-400">{order.title}</div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div>
                            <div className="font-medium text-gray-900 dark:text-white">{order.submitterName}</div>
                            <div className="text-sm text-gray-500 dark:text-secondary-400">{order.divisionName}</div>
                          </div>
                        </td>
                        <td className="px-4 py-3 font-medium">
                          {formatCurrency(order.totalAmount, order.currency)}
                        </td>
                        <td className="px-4 py-3">
                          {formatDate(order.updatedAt)}
                        </td>
                        <td className="px-4 py-3">
                          <Badge variant={getStatusBadgeVariant(order.status)}>
                            {getStatusLabel(order.status)}
                          </Badge>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex justify-end">
                            <a
                              className="text-blue-600 dark:text-blue-400 hover:text-blue-800 p-1"
                              href={`/order-detail/${order.id}`}
                              onClick={(e) => { e.preventDefault(); handleViewOrder(order.id); }}
                              title="View Details"
                            >
                              <i className="fa-solid fa-eye"></i>
                            </a>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {cancelledOrders.length > 0 && totalPages > 1 && (
              <div className="mt-6 flex items-center justify-between">
                <div className="text-sm text-gray-700 dark:text-secondary-200">
                  Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalCount)} of {totalCount} results
                </div>
                <div className="flex space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                  >
                    Previous
                  </Button>
                  <span className="px-3 py-1 text-sm text-gray-700 dark:text-secondary-200">
                    Page {currentPage} of {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </div>
        </Card>
      </div>
    </SidebarLayout>
  );
}
